import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';

import { ConfigService } from '../../../config.service';
import { getContainerProjectId, getRoleMatrix } from '../../container.utils';
import { ContainerAccess } from '../../enums/container-access.enum';
import { PermissionAction } from '../../enums/permission-action.enum';
import { Container } from '../../models/container.model';
import { ProfileService } from '../../../profile/profile.service';
import { ProjectsService } from '../../../projects/projects.service';
import { Auditable, QueriesDialogComponent } from '../../../queries/queries-dialog/queries-dialog.component';
import { File } from '../../../shared/models/file.model';
import { DialogService } from '../../../dialogs';

@Component({
  host: {
    'test-id': 'fw-action-menu',
  },
  selector: 'fw-action-menu',
  templateUrl: './fw-action-menu.component.html',
})
export class FwActionMenuComponent implements OnChanges, OnDestroy {
  @Input() auditable: Auditable;
  @Input() fieldLabel?: string;
  @Input() fieldKey?: string;

  private subscriptions = {
    permissions: Subscription.EMPTY,
  };

  projectId$ = new BehaviorSubject(null);
  breadcrumbs: { label: string }[];
  dialogs = {
    queries: null as MatDialogRef<QueriesDialogComponent>,
  };
  permissions = {
    viewAuditTrail: false,
  };
  permissionSet = new Set<PermissionAction>();

  constructor(
    private config: ConfigService,
    private dialog: DialogService,
    private profileService: ProfileService,
    private projectsService: ProjectsService
  ) { }

  get field(): { label: string, key: string } {
    if (this.fieldLabel && this.fieldKey) {
      return {
        label: this.fieldLabel,
        key: this.fieldKey,
      };
    } else {
      return null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.auditable || changes.fieldLabel || changes.fieldKey) {
      this.loadPermissions();
    }
  }

  ngOnDestroy(): void {
    this.dialogs.queries?.close();

    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  loadPermissions(): void {
    const projectId = (this.auditable as File)?.file_id ? this.auditable.parents.project : getContainerProjectId(this.auditable as Container);
    if (projectId) {
      this.subscriptions.permissions = this.projectsService.getProjectPermissions(projectId).subscribe((permissions) => {
        this.permissionSet = new Set(permissions);
        this.permissions = {
          viewAuditTrail:
            this.permissionSet.has(PermissionAction.ContainersViewMetadata) &&
            this.config.isFeatureEnabled('audit_trail'),
        };
      });
    } else {
      // Fall back to roleMatrix if the container doesn't have roles
      this.subscriptions.permissions = this.profileService.getProfile().pipe(first()).subscribe((profile) => {
        const roleMatrix = getRoleMatrix(this.auditable as Container, profile);
        this.permissions = {
          viewAuditTrail: roleMatrix[ContainerAccess.ReadOnly] && this.config.isFeatureEnabled('audit_trail'),
        };
      });
    }
  }

  viewAuditTrail(): void {
    const data = {
      auditable: this.auditable,
      type: this.field ? 'field' : 'object',
      ...this.field ? { field: this.field } : null,
    };

    this.dialogs.queries = this.dialog.open(QueriesDialogComponent, {
      hasBackdrop: true,
      disableClose: false,
      height: '100%',
      width: '850px',
      panelClass: ['border-radius-0', 'padding-0'],
      position: { right: '0' },
      data,
    });
  }
}
